/**
 * React Query hooks for bridge matches
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { authorizedAxios } from '@moonscape/shared';

export interface BridgeMatch {
  id: number;
  send_txn_id: string;
  receive_txn_id?: string | null;
  send_chain: string;
  receive_chain?: string | null;
  send_wallet: string;
  receive_wallet?: string | null;
  asset: string;
  amount: string;
  bridge_contract: string;
  bridge_contract_id?: number | null;
  bridge_name: string;
  confidence_level: 'high' | 'medium' | 'low' | 'orphaned';
  match_type: 'bridge_transfer' | 'missing_wallet';
  status: 'suggested' | 'confirmed' | 'rejected';
  created_at: string;
  send_transaction: {
    tradeid: string;
    blockchain: string;
    asset: string;
    amount: number;
    timestamp: number;
    contract?: string | null;
  };
  receive_transaction?: {
    tradeid: string | null;
    blockchain: string | null;
    asset: string | null;
    amount: number | null;
    timestamp: number | null;
  } | null;
  timeDifferenceMinutes?: number;
  amountMatchPercentage?: number;

  // Convenience properties (computed from snake_case)
  bridgeName?: string;
  bridgeContractId?: number | null;
  confidenceLevel?: string;
  matchType?: string;
  sendTransaction?: any;
  receiveTransaction?: any;
}

export interface BridgeMatchesPage {
  items: BridgeMatch[];
  cursor: string;
  remaining: number;
}

/**
 * Transform API response to add camelCase convenience properties
 */
function transformBridgeMatch(match: BridgeMatch): BridgeMatch {
  return {
    ...match,
    // Add camelCase aliases for existing components
    bridgeName: match.bridge_name,
    bridgeContractId: match.bridge_contract_id,
    confidenceLevel: match.confidence_level,
    matchType: match.match_type,
    sendTransaction: match.send_transaction,
    receiveTransaction: match.receive_transaction,
  };
}

/**
 * Fetch paginated bridge matches for a user
 */
export function useBridgeMatches(cursor: number | string = 0, filters?: { status?: string }) {
  const { user, getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['bridge-matches', user?.sub, cursor, filters],
    queryFn: async () => {
      console.log('[useBridgeMatches] Fetching bridge matches...', { user: user?.sub, cursor, filters });
      const token = await getAccessTokenSilently();
      const params = {
        cursor: cursor ?? 0,
        limit: 10,
        ...filters
      };
      console.log('[useBridgeMatches] Request params:', params);
      const response = await authorizedAxios(token).get(
        `/bridge-matches`,
        { params }
      );
      console.log('[useBridgeMatches] Response received:', {
        itemCount: response.data?.items?.length,
        cursor: response.data?.cursor,
        remaining: response.data?.remaining,
        firstItem: response.data?.items?.[0]
      });

      // Transform items to add camelCase convenience properties
      const transformedData = {
        ...response.data,
        items: response.data?.items?.map(transformBridgeMatch) || []
      };

      console.log('[useBridgeMatches] Transformed data:', {
        itemCount: transformedData.items.length,
        firstTransformed: transformedData.items[0]
      });

      return transformedData as BridgeMatchesPage;
    },
    enabled: !!user?.sub,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}

/**
 * Confirm a bridge match
 */
export function useConfirmBridgeMatch() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (matchId: number) => {
      const token = await getAccessTokenSilently();
      await authorizedAxios(token).post(
        `/bridge-matches/${matchId}/confirm`,
        { action: 'confirm' }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bridge-matches'] });
      queryClient.invalidateQueries({ queryKey: ['bridge-match-counts'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['tax-calculations'] });
    }
  });
}

/**
 * Reject a bridge match
 */
export function useRejectBridgeMatch() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (matchId: number) => {
      const token = await getAccessTokenSilently();
      await authorizedAxios(token).post(
        `/bridge-matches/${matchId}/confirm`,
        { action: 'reject' }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bridge-matches'] });
      queryClient.invalidateQueries({ queryKey: ['bridge-match-counts'] });
    }
  });
}

/**
 * Bulk confirm multiple matches
 */
export function useBulkConfirmMatches() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (matchIds: number[]) => {
      const token = await getAccessTokenSilently();
      await authorizedAxios(token).post(
        `/bridge-matches/bulk-confirm`,
        { matchIds }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bridge-matches'] });
      queryClient.invalidateQueries({ queryKey: ['bridge-match-counts'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    }
  });
}

/**
 * Bridge match counts by status
 */
export interface BridgeMatchCounts {
  suggested: number;
  confirmed: number;
  rejected: number;
  total: number;
  hasAnyMatches: boolean;
  hasPending: boolean;
  hasHandledAll: boolean;
}

/**
 * Fetch bridge match counts by status
 * Used to determine empty state UI (no matches ever vs all handled)
 */
export function useBridgeMatchCounts() {
  const { user, getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['bridge-match-counts', user?.sub],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get('/bridge-matches/counts');
      return response.data?.data as BridgeMatchCounts;
    },
    enabled: !!user?.sub,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
}
